import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ArrowRightOutlined } from "@ant-design/icons";
import { Button, Typography, theme } from "antd";
import { matchPath, useLocation, useNavigate } from "react-router-dom";

// Non-root routes that get the fixed mobile top navbar. Root routes (the ones
// reachable from the bottom nav / menu) don't have one. `titleParam` makes a
// query param both the match condition and the title (marketplace tag view).
const ROUTES = [
  { path: "/marketplace", titleParam: "tag", title: "فروشگاه", backTo: "/marketplace" },
  { path: "/videos/new", title: "آپلود ویدیو", backTo: "/videos" },
  { path: "/videos/:id", title: "ویرایش ویدیو", backTo: "/videos" },
  { path: "/finance/withdraw", title: "برداشت", backTo: "/finance" },
  { path: "/finance/advance", title: "دریافت وام", backTo: "/finance" },
  { path: "/marketplace/products/:id", title: "محصول", backTo: "/marketplace" },
  { path: "/contracts/:uuid", title: "قرارداد", backTo: "/settings" },
];

export const MOBILE_TOP_NAV_HEIGHT = 52;

const MobileNavContext = createContext({
  titleOverride: null,
  setTitleOverride: () => {},
});

export function MobileNavProvider({ children }) {
  const [titleOverride, setTitleOverride] = useState(null);
  const value = useMemo(() => ({ titleOverride, setTitleOverride }), [titleOverride]);
  return <MobileNavContext.Provider value={value}>{children}</MobileNavContext.Provider>;
}

// Pages with a dynamic title (product, video, contract) call this once the
// entity is loaded; falls back to the route's static title until then.
export function useMobileNavTitle(title) {
  const { setTitleOverride } = useContext(MobileNavContext);
  useEffect(() => {
    if (!title) return undefined;
    setTitleOverride(title);
    return () => setTitleOverride(null);
  }, [title, setTitleOverride]);
}

export function useMobileNavRoute() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const route = ROUTES.find(
    (r) =>
      matchPath(r.path, location.pathname) &&
      (!r.titleParam || searchParams.has(r.titleParam))
  );
  if (!route) return null;
  if (route.titleParam) {
    return { ...route, title: searchParams.get(route.titleParam) || route.title };
  }
  return route;
}

function MobileTopNav() {
  const route = useMobileNavRoute();
  const navigate = useNavigate();
  const { titleOverride } = useContext(MobileNavContext);
  const { token } = theme.useToken();

  if (!route) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 900,
        height: MOBILE_TOP_NAV_HEIGHT,
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: "0 8px",
        backgroundColor: token.colorBgContainer,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        boxShadow: token.boxShadowTertiary,
      }}
    >
      <Button
        type="text"
        icon={<ArrowRightOutlined />}
        onClick={() => navigate(route.backTo)}
        aria-label="بازگشت"
      />
      <Typography.Text strong ellipsis style={{ fontSize: 16, flex: 1 }}>
        {titleOverride || route.title}
      </Typography.Text>
    </div>
  );
}

export default MobileTopNav;
